import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { useTheme } from '../../theme/ThemeContext';

interface CustomDatePickerProps {
  value: string;
  onChange: (date: string) => void;
  placeholder?: string;
  minDate?: string; // Dates before this will be disabled
  maxDate?: string; // Dates after this will be disabled
  availableDates?: string[]; // If provided, only these dates will be enabled
}

export const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  value,
  onChange,
  placeholder = 'Select date',
  minDate,
  maxDate,
  availableDates,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (value) {
      return new Date(value + 'T00:00:00');
    }
    return new Date();
  });
  const { theme } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const formatDisplayDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      if (direction === 'prev') {
        newMonth.setMonth(newMonth.getMonth() - 1);
      } else {
        newMonth.setMonth(newMonth.getMonth() + 1);
      }
      return newMonth;
    });
  };

  const isDateDisabled = (date: Date): boolean => {
    // If availableDates is provided, only allow dates that are in the list
    if (availableDates && availableDates.length > 0) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      return !availableDates.includes(dateStr);
    }

    // Otherwise, use min/max date validation
    if (minDate) {
      const minDateTime = new Date(minDate + 'T00:00:00');
      if (date < minDateTime) return true;
    }
    if (maxDate) {
      // Normalize the date to start of day for comparison
      const dateStartOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const maxDateStartOfDay = new Date(maxDate + 'T00:00:00');
      if (dateStartOfDay > maxDateStartOfDay) return true;
    }
    return false;
  };

  const handleDateClick = (date: Date) => {
    if (isDateDisabled(date)) return;

    // Fix timezone issue by using local date formatting instead of UTC
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    onChange(dateStr);
    setIsOpen(false);
  };

  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    // Get first day of the month and adjust for Monday start
    const firstDay = new Date(year, month, 1);
    const firstDayWeekday = (firstDay.getDay() + 6) % 7; // Convert Sunday=0 to Monday=0

    // Get last day of the month
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const selectedDate = value ? new Date(value + 'T00:00:00') : null;

    const days = [];

    // Add previous month's trailing days
    const prevMonth = new Date(year, month - 1, 0);
    for (let i = firstDayWeekday - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonth.getDate() - i);
      const isDisabled = isDateDisabled(date);

      days.push(
        <div key={`prev-${prevMonth.getDate() - i}`} className="relative group">
          <button
            onClick={() => handleDateClick(date)}
            disabled={isDisabled}
            className={`
              w-8 h-8 text-xs rounded-md transition-all duration-200 font-medium
              text-gray-400 dark:text-gray-600
              ${
                isDisabled
                  ? 'cursor-not-allowed opacity-30'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700/50 cursor-pointer active:scale-95'
              }
            `}
          >
            {date.getDate()}
          </button>
          {isDisabled && (
            <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-50">
              <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs px-2 py-1 rounded-lg shadow-lg whitespace-nowrap">
                No benchmark data available
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
              </div>
            </div>
          )}
        </div>,
      );
    }

    // Add current month's days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isToday = date.getTime() === today.getTime();
      const isSelected = selectedDate && date.getTime() === selectedDate.getTime();
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      const isDisabled = isDateDisabled(date);

      days.push(
        <div key={day} className="relative group">
          <button
            onClick={() => handleDateClick(date)}
            disabled={isDisabled}
            className={`
              w-8 h-8 text-xs rounded-md transition-all duration-200 font-medium relative
              ${
                isDisabled
                  ? 'text-gray-300 dark:text-gray-700 cursor-not-allowed opacity-30'
                  : isWeekend
                    ? 'text-red-400 dark:text-red-400'
                    : theme === 'dark'
                      ? 'text-gray-200'
                      : 'text-gray-800'
              }
              ${
                isSelected
                  ? 'bg-blue-500 text-white shadow-md z-10'
                  : isToday
                    ? 'bg-gray-100 dark:bg-gray-700 ring-1 ring-blue-500/30'
                    : !isDisabled
                      ? 'hover:bg-blue-200 dark:hover:bg-blue-800/40 hover:text-blue-700 dark:hover:text-blue-300 hover:scale-105 cursor-pointer active:scale-95'
                      : ''
              }
            `}
          >
            {date.getDate()}
          </button>
          {isDisabled && (
            <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-50">
              <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs px-2 py-1 rounded-lg shadow-lg whitespace-nowrap">
                No benchmark data available
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
              </div>
            </div>
          )}
        </div>,
      );
    }

    // Add next month's leading days
    const remainingCells = 42 - days.length; // 6 rows × 7 days
    for (let day = 1; day <= remainingCells; day++) {
      const date = new Date(year, month + 1, day);
      const isDisabled = isDateDisabled(date);

      days.push(
        <div key={`next-${day}`} className="relative group">
          <button
            onClick={() => handleDateClick(date)}
            disabled={isDisabled}
            className={`
              w-8 h-8 text-xs rounded-md transition-all duration-200 font-medium
              text-gray-400 dark:text-gray-600
              ${
                isDisabled
                  ? 'cursor-not-allowed opacity-30'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700/50 cursor-pointer active:scale-95'
              }
            `}
          >
            {date.getDate()}
          </button>
          {isDisabled && (
            <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-50">
              <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs px-2 py-1 rounded-lg shadow-lg whitespace-nowrap">
                No benchmark data available
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
              </div>
            </div>
          )}
        </div>,
      );
    }

    return days;
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full flex items-center justify-between rounded-md border-2 transition-all duration-200
          ${
            theme === 'dark'
              ? 'bg-gray-800 border-gray-600 text-gray-200 hover:border-gray-500'
              : 'bg-white border-gray-300 text-gray-800 hover:border-gray-400'
          }
          ${isOpen ? 'ring-2 ring-blue-500/20' : ''}
        `}
        style={{
          padding: '6px 10px', // Match the smaller dropdown padding
          fontFamily: 'system-ui, -apple-system, sans-serif', // Uniform font
          fontSize: '12px',
          fontWeight: '500',
        }}
      >
        <span className={value ? '' : 'text-gray-400 dark:text-gray-500'}>
          {value ? formatDisplayDate(value) : placeholder}
        </span>
        <div className="ml-3">
          <Calendar
            size={14}
            className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
          />
        </div>
      </button>

      {isOpen && (
        <div
          className={`
          absolute top-full left-0 mt-2 p-4 rounded-xl shadow-2xl border z-50
          ${theme === 'dark' ? 'bg-gray-900 border-gray-600' : 'bg-gray-50 border-gray-200'}
          w-72 animate-in slide-in-from-top-2 duration-200
        `}
        >
          {/* Month/Year Header with Navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={() => navigateMonth('prev')}
              className={`
                p-1.5 rounded-lg transition-all duration-200 hover:scale-110
                ${
                  theme === 'dark'
                    ? 'hover:bg-gray-700 text-gray-300'
                    : 'hover:bg-gray-100 text-gray-600'
                }
              `}
            >
              <ChevronLeft size={16} />
            </button>

            <div
              className={`
              flex items-center gap-1 font-semibold text-base cursor-pointer px-2 py-1 rounded-lg
              ${
                theme === 'dark'
                  ? 'text-gray-200 hover:bg-gray-700'
                  : 'text-gray-800 hover:bg-gray-100'
              }
            `}
            >
              {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              <ChevronDown size={14} className="text-gray-400" />
            </div>

            <button
              type="button"
              onClick={() => navigateMonth('next')}
              className={`
                p-1.5 rounded-lg transition-all duration-200 hover:scale-110
                ${
                  theme === 'dark'
                    ? 'hover:bg-gray-700 text-gray-300'
                    : 'hover:bg-gray-100 text-gray-600'
                }
              `}
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Weekday headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {[
              { day: 'Mo', isWeekend: false },
              { day: 'Tu', isWeekend: false },
              { day: 'We', isWeekend: false },
              { day: 'Th', isWeekend: false },
              { day: 'Fr', isWeekend: false },
              { day: 'Sa', isWeekend: true },
              { day: 'Su', isWeekend: true },
            ].map((dayInfo) => (
              <div
                key={dayInfo.day}
                className={`
                  text-center text-xs font-semibold py-1
                  ${
                    dayInfo.isWeekend
                      ? 'text-red-400 dark:text-red-400'
                      : theme === 'dark'
                        ? 'text-gray-400'
                        : 'text-gray-500'
                  }
                `}
              >
                {dayInfo.day}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="grid grid-cols-7 gap-1">{renderCalendar()}</div>
        </div>
      )}
    </div>
  );
};
